import { Component, computed, inject, Input } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NbToastrService } from '@nebular/theme';
import { Attachment, attachmentsFromDetails, attachmentToMerge } from '@shared/form-controls/attachment-upload/attachment-utils';
import { toSignal } from '@angular/core/rxjs-interop';
import { AttachmentMergeDto } from '@artemis-software/wr-api';

@Component({
  selector: 'wr-finish-maintenance-timer-dialog',
  standalone: false,
  templateUrl: './finish-maintenance-timer-dialog.component.html',
  styleUrls: ['./finish-maintenance-timer-dialog.component.scss'],
})
export class FinishMaintenanceTimerDialogComponent {
  private fb = inject(FormBuilder);
  private toastr = inject(NbToastrService);

  @Input() title?: string;
  @Input() userId?: string;
  @Input() mandatory = true;

  form: FormGroup = this.fb.group({
    name: [''],
    comment: [''],
    attachments: [attachmentsFromDetails([])],
    sendEmailToPropertyManager: [false],
  });

  readonly valueSignal = toSignal(this.form.valueChanges, {
    initialValue: this.form.value,
  });

  readonly userIdProvided = computed(() => !!this.userId && this.userId.trim().length > 0);

  readonly nameValue = computed(() => this.valueSignal().name);

  readonly attachments = computed(() => this.valueSignal().attachments ?? []);

  readonly nameWasProvided = computed(() => {
    const hasUserId = !!this.userId;
    const hasName = !!this.nameValue();
    return hasUserId || hasName;
  });

  readonly canSubmit = computed(() => {
    const attachments: AttachmentMergeDto[] = this.attachments() ?? [];
    const hasUserId = !!this.userId;
    const hasName = !!this.nameValue();
    const nameOrUserProvided = hasUserId || hasName;

    if (!nameOrUserProvided) return false;
    if (this.mandatory) return attachments.some(a => !a.deleted);
    return true;
  });

  readonly attachmentsRequired = computed(() => {
    const attachments: AttachmentMergeDto[] = this.attachments() ?? [];
    if (!this.mandatory) return true;
    return attachments.some(a => !a.deleted);
  });

  submit() {
    const value = this.form.value;

    const userId = this.userId ?? '';
    const comment = value.comment ?? '';
    const name = value.name ?? '';
    const attachments = (value.attachments ?? [])
      .filter((a: Attachment) => !a.deleted)
      .map((dto: Attachment) => attachmentToMerge(dto));

    if (!userId && (!name || name.trim().length === 0)) {
      this.toastr.warning('Bitte geben Sie einen Namen ein.', 'Benutzer erforderlich');
      return;
    }

    if (this.mandatory && attachments.length === 0) {
      this.toastr.warning('Bitte laden Sie mindestens ein Bild hoch.', 'Bilder erforderlich');
      return;
    }

    return {
      userId,
      comment,
      name,
      attachments,
      sendEmailToPropertyManager: value.sendEmailToPropertyManager ?? false,
    };
  }
}
